import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Heart, Users, FolderOpen, Newspaper, 
  Mail, LogOut, Menu, X, Plus, Edit, Trash2, Eye, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Login Component
const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      login(response.data);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="admin-login">
      <Card className="max-w-md w-full bg-white rounded-2xl border-none shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-saffron rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-heading text-2xl">ॐ</span>
            </div>
            <h1 className="font-heading text-2xl text-gray-900">Admin Login</h1>
            <p className="font-body text-sm text-gray-500">Dharma Astra Association</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="font-body text-sm">Email</Label>
              <Input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                placeholder="admin@dharmastra.org"
                className="mt-1 py-3 rounded-lg"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm">Password</Label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1 py-3 rounded-lg"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-full py-3 font-body"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const AdminSidebar = ({ isOpen, setIsOpen }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/donations', icon: Heart, label: 'Donations' },
    { path: '/admin/volunteers', icon: Users, label: 'Volunteers' },
    { path: '/admin/projects', icon: FolderOpen, label: 'Projects' },
    { path: '/admin/news', icon: Newspaper, label: 'News' },
    { path: '/admin/contacts', icon: Mail, label: 'Contacts' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`fixed top-0 left-0 h-full bg-terracotta text-white z-50 transition-transform lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} w-64`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="font-heading text-lg">ॐ</span>
              </div>
              <div>
                <h1 className="font-heading text-lg">Dharma Astra</h1>
                <p className="text-xs text-white/70">Admin Panel</p>
              </div>
            </div>
            <button className="lg:hidden" onClick={() => setIsOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-body text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full hover:bg-white/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-body text-sm">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState({
    donations: 0,
    amount: 0,
    volunteers: 0,
    projects: 0,
  });

  const { admin } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [donationStats, volunteerCount, projects] = await Promise.all([
          axios.get(`${API}/donations/stats`),
          axios.get(`${API}/volunteers/count`),
          axios.get(`${API}/projects`),
        ]);
        setStats({
          donations: donationStats.data.total_donations,
          amount: donationStats.data.total_amount,
          volunteers: volunteerCount.data.count,
          projects: projects.data.length,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl text-gray-900">Dashboard</h1>
        <p className="font-body text-gray-500">Welcome back, {admin?.name}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white rounded-xl border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500">Total Donations</p>
                <p className="font-heading text-3xl text-gray-900">{stats.donations}</p>
              </div>
              <div className="w-12 h-12 bg-saffron/10 rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-saffron" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500">Amount Raised</p>
                <p className="font-heading text-3xl text-gray-900">₹{(stats.amount / 100000).toFixed(1)}L</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">₹</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500">Volunteers</p>
                <p className="font-heading text-3xl text-gray-900">{stats.volunteers}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500">Active Projects</p>
                <p className="font-heading text-3xl text-gray-900">{stats.projects}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Donations List
const DonationsList = () => {
  const { admin } = useAuth();
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const response = await axios.get(`${API}/donations`, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        setDonations(response.data);
      } catch (error) {
        toast.error('Failed to fetch donations');
      } finally {
        setLoading(false);
      }
    };
    if (admin?.token) fetchDonations();
  }, [admin]);

  return (
    <div className="space-y-6" data-testid="donations-list">
      <h1 className="font-heading text-3xl text-gray-900">Donations</h1>
      
      <Card className="bg-white rounded-xl border-none shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body">Donor Name</TableHead>
              <TableHead className="font-body">Email</TableHead>
              <TableHead className="font-body">Amount</TableHead>
              <TableHead className="font-body">Type</TableHead>
              <TableHead className="font-body">Status</TableHead>
              <TableHead className="font-body">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {donations.map((donation) => (
              <TableRow key={donation.id}>
                <TableCell className="font-body font-medium">{donation.donor_info.full_name}</TableCell>
                <TableCell className="font-body text-sm text-gray-500">{donation.donor_info.email}</TableCell>
                <TableCell className="font-body font-semibold text-saffron">₹{donation.amount.toLocaleString()}</TableCell>
                <TableCell className="font-body text-sm capitalize">{donation.donation_type}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-body ${
                    donation.status === 'completed' || donation.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {donation.status}
                  </span>
                </TableCell>
                <TableCell className="font-body text-sm text-gray-500">
                  {new Date(donation.created_at).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {donations.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500 font-body">No donations yet</div>
        )}
      </Card>
    </div>
  );
};

// Volunteers List
const VolunteersList = () => {
  const { admin } = useAuth();
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVolunteers = async () => {
      try {
        const response = await axios.get(`${API}/volunteers`, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        setVolunteers(response.data);
      } catch (error) {
        toast.error('Failed to fetch volunteers');
      } finally {
        setLoading(false);
      }
    };
    if (admin?.token) fetchVolunteers();
  }, [admin]);

  return (
    <div className="space-y-6" data-testid="volunteers-list">
      <h1 className="font-heading text-3xl text-gray-900">Volunteers</h1>
      
      <Card className="bg-white rounded-xl border-none shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body">Name</TableHead>
              <TableHead className="font-body">Email</TableHead>
              <TableHead className="font-body">Phone</TableHead>
              <TableHead className="font-body">Location</TableHead>
              <TableHead className="font-body">Interests</TableHead>
              <TableHead className="font-body">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {volunteers.map((volunteer) => (
              <TableRow key={volunteer.id}>
                <TableCell className="font-body font-medium">{volunteer.full_name}</TableCell>
                <TableCell className="font-body text-sm text-gray-500">{volunteer.email}</TableCell>
                <TableCell className="font-body text-sm">{volunteer.phone}</TableCell>
                <TableCell className="font-body text-sm">{volunteer.city_state}</TableCell>
                <TableCell className="font-body text-xs">
                  {volunteer.areas_of_interest.slice(0, 2).join(', ')}
                  {volunteer.areas_of_interest.length > 2 && '...'}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-body ${
                    volunteer.status === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {volunteer.status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {volunteers.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500 font-body">No volunteers yet</div>
        )}
      </Card>
    </div>
  );
};

// Projects Management
const ProjectsManagement = () => {
  const { admin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    category: 'religious',
    target_amount: '',
    raised_amount: 0,
    image_url: '',
    is_featured: false,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        target_amount: parseInt(formData.target_amount),
        raised_amount: parseInt(formData.raised_amount) || 0,
      };

      if (editProject) {
        await axios.put(`${API}/projects/${editProject.id}`, payload, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/projects`, payload, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        toast.success('Project created');
      }
      
      setDialogOpen(false);
      setEditProject(null);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      category: 'religious',
      target_amount: '',
      raised_amount: 0,
      image_url: '',
      is_featured: false,
    });
  };

  const openEditDialog = (project) => {
    setEditProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      location: project.location,
      category: project.category,
      target_amount: project.target_amount.toString(),
      raised_amount: project.raised_amount,
      image_url: project.image_url,
      is_featured: project.is_featured,
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="projects-management">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-3xl text-gray-900">Projects</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditProject(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-6 font-body">
              <Plus className="w-4 h-4 mr-2" /> Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editProject ? 'Edit Project' : 'Add New Project'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="font-body text-sm">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="religious">Religious</SelectItem>
                      <SelectItem value="social">Social</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm">Target Amount (₹)</Label>
                  <Input
                    type="number"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Raised Amount (₹)</Label>
                  <Input
                    type="number"
                    value={formData.raised_amount}
                    onChange={(e) => setFormData({ ...formData, raised_amount: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="font-body text-sm">Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                />
                <Label htmlFor="featured" className="font-body text-sm">Featured Project</Label>
              </div>
              <Button type="submit" className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-lg py-3 font-body">
                {editProject ? 'Update Project' : 'Create Project'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="bg-white rounded-xl border-none shadow-sm overflow-hidden">
            <img src={project.image_url} alt={project.title} className="w-full h-40 object-cover" />
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-heading text-lg text-gray-900 line-clamp-1">{project.title}</h3>
                {project.is_featured && (
                  <span className="px-2 py-0.5 bg-saffron/10 text-saffron text-xs rounded-full">Featured</span>
                )}
              </div>
              <p className="font-body text-sm text-gray-500 mb-3 line-clamp-2">{project.description}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(project)}
                  className="flex-1 rounded-lg text-sm"
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(project.id)}
                  className="rounded-lg text-sm text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// News Management
const NewsManagement = () => {
  const { admin } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editNews, setEditNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    is_published: true,
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news?published_only=false`);
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editNews) {
        await axios.put(`${API}/news/${editNews.id}`, formData, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        toast.success('News updated');
      } else {
        await axios.post(`${API}/news`, formData, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        toast.success('News created');
      }
      
      setDialogOpen(false);
      setEditNews(null);
      resetForm();
      fetchNews();
    } catch (error) {
      toast.error('Failed to save news');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/news/${id}`, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('News deleted');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', image_url: '', is_published: true });
  };

  return (
    <div className="space-y-6" data-testid="news-management">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-3xl text-gray-900">News & Updates</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setEditNews(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-6 font-body">
              <Plus className="w-4 h-4 mr-2" /> Add News
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editNews ? 'Edit News' : 'Add News'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="font-body text-sm">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Image URL (Optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                />
                <Label className="font-body text-sm">Published</Label>
              </div>
              <Button type="submit" className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-lg py-3 font-body">
                {editNews ? 'Update' : 'Create'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      <Card className="bg-white rounded-xl border-none shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body">Title</TableHead>
              <TableHead className="font-body">Status</TableHead>
              <TableHead className="font-body">Date</TableHead>
              <TableHead className="font-body">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {news.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-body font-medium">{item.title}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    item.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {item.is_published ? 'Published' : 'Draft'}
                  </span>
                </TableCell>
                <TableCell className="font-body text-sm text-gray-500">
                  {new Date(item.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => {
                      setEditNews(item);
                      setFormData(item);
                      setDialogOpen(true);
                    }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Contacts List
const ContactsList = () => {
  const { admin } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get(`${API}/contacts`, {
          headers: { Authorization: `Bearer ${admin.token}` }
        });
        setContacts(response.data);
      } catch (error) {
        toast.error('Failed to fetch contacts');
      } finally {
        setLoading(false);
      }
    };
    if (admin?.token) fetchContacts();
  }, [admin]);

  return (
    <div className="space-y-6" data-testid="contacts-list">
      <h1 className="font-heading text-3xl text-gray-900">Contact Messages</h1>
      
      <Card className="bg-white rounded-xl border-none shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body">Name</TableHead>
              <TableHead className="font-body">Email</TableHead>
              <TableHead className="font-body">Subject</TableHead>
              <TableHead className="font-body">Message</TableHead>
              <TableHead className="font-body">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="font-body font-medium">{contact.name}</TableCell>
                <TableCell className="font-body text-sm text-gray-500">{contact.email}</TableCell>
                <TableCell className="font-body text-sm">{contact.subject}</TableCell>
                <TableCell className="font-body text-sm text-gray-500 max-w-xs truncate">{contact.message}</TableCell>
                <TableCell className="font-body text-sm text-gray-500">
                  {new Date(contact.created_at).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {contacts.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500 font-body">No messages yet</div>
        )}
      </Card>
    </div>
  );
};

// Admin Layout
const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button 
              className="lg:hidden p-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4">
              <Link to="/" className="font-body text-sm text-saffron hover:underline">
                View Website
              </Link>
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Main Admin Page Component
const AdminPage = () => {
  const { admin } = useAuth();
  const location = useLocation();

  // If not logged in and not on login page, show login
  if (!admin && location.pathname !== '/admin') {
    return <AdminLogin />;
  }

  // If on /admin path without being logged in, show login
  if (!admin) {
    return <AdminLogin />;
  }

  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/donations" element={<DonationsList />} />
        <Route path="/volunteers" element={<VolunteersList />} />
        <Route path="/projects" element={<ProjectsManagement />} />
        <Route path="/news" element={<NewsManagement />} />
        <Route path="/contacts" element={<ContactsList />} />
      </Routes>
    </AdminLayout>
  );
};

export default AdminPage;
